import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MdOutlineArrowBackIos } from "react-icons/md";
import { FiTrash2 } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { fetchBlogById, updateBlog, deleteBlog, createBlog } from "../../redux/slices/blogsCommunitySlice";
import { FiEdit3 } from "react-icons/fi";
import DeleteModal from "../../components/common/DeleteModal";
import { toast } from "react-toastify";

export default function BlogEdit() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isEditing = id && id !== "new";
  const { detail: blog, detailStatus } = useSelector((state) => state.blogsCommunity);

  const [formData, setFormData] = useState({
    title: "",
    sub_title: "",
    category: "",
    content: "",
    user_id: "",
    status: "draft"
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (isEditing) {
      dispatch(fetchBlogById(id));
    }
  }, [dispatch, id, isEditing]);

  useEffect(() => {
    if (blog && isEditing) {
      setFormData({
        title: blog.title || "",
        sub_title: blog.sub_title || "",
        category: blog.category || "",
        content: blog.content || "",
        user_id: blog.author?.uuid || "",
        status: blog.status || "draft"
      });
    }
  }, [blog, isEditing]);

  if (isEditing && detailStatus === "loading") return <div className="px-20 pt-10">Loading...</div>;
  if (isEditing && detailStatus === "failed") return <div className="px-20 pt-10">Failed to load blog data.</div>;
  if (isEditing && !blog) return <div className="px-20 pt-10">Blog not found.</div>;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedImages(prev => [...prev, ...files]);
  };

  const handleSave = async () => {
    console.log('BlogEdit: Saving blog data:', {
      id,
      isEditing,
      formData,
      selectedImages: selectedImages.length
    });

    try {
      const formDataToSend = new FormData();

      // Required fields for both create and edit
      if (formData.title) formDataToSend.append('blog[title]', formData.title);
      if (formData.sub_title) formDataToSend.append('blog[sub_title]', formData.sub_title);
      if (formData.category) formDataToSend.append('blog[category]', formData.category);
      if (formData.content) formDataToSend.append('blog[content]', formData.content);

      // User ID - different handling for create vs edit
      if (!isEditing && formData.user_id) {
        formDataToSend.append('blog[user_id]', formData.user_id);
      } else if (isEditing && blog.author?.uuid) {
        formDataToSend.append('blog[vet_id]', blog.author.uuid);
      }

      // Status - only for creation
      if (!isEditing && formData.status) {
        formDataToSend.append('blog[status]', formData.status);
      }

      // Images - only for creation
      if (!isEditing && selectedImages.length > 0) {
        selectedImages.forEach((image) => {
          formDataToSend.append('blog[images][]', image);
        });
      }

      console.log('BlogEdit: FormData:');
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}: ${value}`);
      }

      if (isEditing) {
        await dispatch(updateBlog({ id, data: formDataToSend })).unwrap();
        toast.success('Blog updated successfully!');
      } else {
        await dispatch(createBlog(formDataToSend)).unwrap();
        toast.success('Blog created successfully!');
      }

      navigate("/swift-app/admin/blogsCommunity");
    } catch (error) {
      console.error('BlogEdit: Save failed:', error);
      const errorMessage = error?.response?.data?.meta?.message || `Failed to ${isEditing ? 'update' : 'create'} blog. Please try again.`;
      toast.error(errorMessage);
    }
  };

  const handleDelete = () => {
    dispatch(deleteBlog(id));
    setModalOpen(false);
    navigate(-2); // Go back after delete
  };

  return (
    <div className="relative min-h-screen bg-white font-inter px-6 py-12">
      {/* Top bar */}
      <div className="flex justify-between items-start mb-10">
        <div className="flex items-start">
          <button
            onClick={() => navigate(-1)}
            aria-label="Go Back"
            className="mt-1 mr-3 p-2 text-2xl text-gray-800 hover:text-blue-600"
          >
            <MdOutlineArrowBackIos />
          </button>
          <div>
            <div className="text-xl font-bold text-black">
              {isEditing ? `Edit Blog: ${formData.title}` : "Create New Blog"}
            </div>
            <div className="text-gray-600 text-sm mt-1">
              {isEditing ? "Update blog information and content" : "Create a new blog post"}
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          {/* Delete Button - only show for editing */}
          {isEditing && (
            <button
              onClick={() => setModalOpen(true)}
              className="p-2 text-gray-600 hover:text-red-500"
            >
              <FiTrash2 size={20} />
            </button>
          )}

          <button
            onClick={() => navigate(-1)}
            className="border border-gray-300 rounded-lg px-4 py-2 text-base font-medium text-gray-700 bg-white hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-[#347DFF] text-white rounded-lg px-5 py-2 text-base font-medium hover:bg-blue-600 transition"
          >
            {isEditing ? "Save Changes" : "Create Blog"}
          </button>
        </div>
      </div>

      {/* Form Sections */}
      <div className="space-y-10 px-10">
        {/* Basic Info */}
        <div>
          <div className="flex items-center gap-1 text-gray-700 font-medium mb-5">
            <span className="rounded p-1">
              <FiEdit3 size={22} />
            </span>{" "}
            Blog Information
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <input
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter blog title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sub Title</label>
              <input
                name="sub_title"
                value={formData.sub_title}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter blog sub title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <input
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter category"
              />
            </div>

            {/* Different fields for create vs edit mode */}
            {isEditing ? (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">Author</label>
                  <div className="px-3 py-2 bg-gray-300/80 border border-gray-200 rounded-lg text-gray-700">
                    {blog.author?.name || "N/A"}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Vet Id</label>
                  <div className="px-3 py-2 bg-gray-300/80 border border-gray-200 rounded-lg text-gray-700">
                    {blog.author?.uuid || "N/A"}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">Author User ID</label>
                  <input
                    name="user_id"
                    value={formData.user_id}
                    onChange={handleChange}
                    className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter veterinarian user ID"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Images Section - only for creation */}
        {!isEditing && (
          <div>
            <div className="flex items-center gap-1 text-gray-700 font-medium mb-5">
              <span className="rounded p-1">📷</span> Blog Images
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Upload Blog Images (Optional)</label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {selectedImages.length > 0 && (
                <div className="mt-4">
                  <div className="text-sm text-gray-600 mb-2">
                    {selectedImages.length} image(s) selected
                  </div>
                  <div className="flex flex-wrap gap-4">
                    {selectedImages.map((image, index) => (
                      <img
                        key={index}
                        src={URL.createObjectURL(image)}
                        alt={`Preview ${index + 1}`}
                        className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content Section */}
        <div>
          <div className="flex items-center gap-1 text-gray-700 font-medium mb-5">
            <span className="rounded p-1">📝</span> Content
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Blog Content</label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              rows="10"
              className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
              placeholder="Enter blog content..."
            />
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <DeleteModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onDelete={handleDelete}
        profileType="Blog"
      />
    </div>
  );
}
